<script setup>
import { computed } from "vue";
import { useModals } from "@/composables/useModals";
import BaseButtonExchange from "@/components/ui/BaseButtonExchange.vue";
import BaseTooltip from "@/components/ui/BaseTooltip.vue";

const { openModal } = useModals();

const props = defineProps({
	prize: {
		type: Object,
		required: true,
	},
	isMobile: {
		type: Boolean,
		default: false,
	},
	isShowExchange: {
		type: Boolean,
		default: false,
	}
});

const prizePrize = computed(() => props.isShowExchange ? props.prize : props.prize.prize);
const isShowCounter = computed(() => props.prize.counter >= 2 && props.prize.prize.format === 'physical');

</script>
<template>
	<div class="slide" >
		<span class="slide__counter" v-if="isShowCounter">x{{ props.prize.counter }}</span>
		<div class="slide__container" @click="prizePrize.format !== 'points' && openModal('modalUserPrize', { content: props.prize, size: 'w1212' })">
			<div class="slide__image" >
				<img width="236" height="236" v-if="prizePrize.images?.length" :src="prizePrize.images[0]" :alt="prizePrize.title" />
			</div>
			<button class="slide__info" type="button" v-if="prizePrize.format !== 'points'">
				<svg width="32" height="32"><use xlink:href="#info"></use></svg>
			</button>
		</div>
		<div class="slide__text">
			<p class="slide__title">{{ prizePrize.title }}</p>
			<div class="slide__price" v-if="props.isShowExchange">
				<span class="slide__price-value">{{ prizePrize.points || 0 }}</span>
				<span class="slide__price-icon">
					<svg width="32" height="32" class="leaf">
						<use xlink:href="#leaf"></use>
					</svg>
				</span>
			</div>
		</div>
		<BaseButtonExchange v-if="props.isShowExchange" class="slide__btn --small">Выбрать подарок</BaseButtonExchange>
	</div>
</template>
<style lang="scss" scoped>
@use "@/assets/scss/base/vars";

.slide {
	display: flex;
	flex-direction: column;
	gap: 0rem;
	align-items: flex-start;
	width: 100%;
	height: 100%;
	&__counter {
		position: absolute;
		font-weight: 500;
		font-size: 1.6rem;
		line-height: 140%;
		
		top: 1.6rem;
		left: 1.6rem;
		padding: 0.2rem 0.9rem 0px;
		border-radius: 1.4rem;
		border: 1px solid rgba(255, 255, 255, 0.24);

		z-index: 1;

		@media (max-width: vars.$x480) {
			top: 1rem;
			left: 1rem;
			padding: 0rem 0.9rem 0.2px;
		}
	}
	&__container {
		display: flex;
		align-items: center;
		justify-content: center;
		position: relative;
		width: 100%;
		// height: 100%;
		border-radius: 4rem;
		border: 1px solid var(--border-accent-2-4);
		background: rgba(255, 255, 255, 0.01);
		backdrop-filter: blur(0.77rem);
		box-shadow: inset 0 1.3rem 3.48rem 0 rgba(255, 255, 255, 0.14);
		aspect-ratio: 1/1;
		margin-bottom: 1.5rem;
		cursor: pointer;
		@media (max-width: vars.$x480) {
			border-radius: 2.4rem;
			margin-bottom: 0.8rem;
		}
		@media (hover: hover) {
			&:hover {
				.slide__info {
					opacity: 0.7;
				}
			}
		}
	}

	&__image {
		width: 23.6rem;
		height: 23.6rem;
		overflow: hidden;

		@media (max-width: vars.$x480) {
			width: 14.6rem;
			height: 14.6rem;
		}

		img {
			width: 100%;
			height: 100%;
			object-fit: contain;
		}
	}

	&__info {
		position: absolute;
		top: 1.5rem;
		right: 1.5rem;
		width: 2.4rem;
		height: 2.4rem;
		cursor: pointer;
		background: transparent;
		border: none;
		padding: 0;
		transition: opacity var(--transition);

		@media (hover: hover) {
			&:hover {
				svg {

					opacity: 0.7;
				}
			}
		}

		@media (max-width: vars.$x480) {
			top: 1rem;
			right: 1rem;
			width: 2rem;
			height: 2rem;
		}

		svg {
			width: 100%;
			height: 100%;
			stroke: transparent;
		}
	}

	&__text {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		gap: 0rem;
		padding: 0 1.2rem 0.5rem;
		width: 100%;
		height: 100%;
		@media (max-width: vars.$x480) {
			gap: 0rem;
			padding: 0 0.8rem;
		}
	}

	&__title {
		font-family: var(--font-family);
		font-weight: 500;
		font-size: 2rem;
		line-height: 1.2;
		color: var(--text-inverse-100);
		max-width: 100%;
		// white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		@media (max-width: vars.$x480) {
			font-size: 1.4rem;
		}
	}

	&__price {
		display: flex;
		align-items: center;
		gap: 0.8rem;
	}

	&__price-value {
		font-family: var(--second-family);
		font-weight: 700;
		font-size: 4.8rem;
		line-height: 140%;
		color: var(--text-accent-2-100);

		@media (max-width: vars.$x480) {
			font-size: 2.8rem;
		}
	}

	&__price-icon {
		width: 3.2rem;
		height: 3.2rem;
		display: flex;
		align-items: center;
		justify-content: center;

		@media (max-width: vars.$x480) {
			width: 2.6rem;
			height: 2.6rem;
		}
	}

	&__btn {
		width: 100%;
		margin-top: auto;
		@media (max-width: vars.$x480) {
			font-size: 1.6rem;
			min-height: 3.2rem;
		}
	}
}
</style>
